/**
 * Product Hub — the whole delivery answer for one product.
 *
 * `resolveFulfillment` layers four sources of truth, most specific first: **the store's override →
 * the variant's own settings → the product's settings → the shipping rule** (§41-42). The rule half
 * lives in `productHub.shipping.ts`, which owns the ladder; this file owns what happens *around* it,
 * and the seam is the one the spec itself draws — §38-40/§45 are about rules, §41-42/§46-47 are about
 * the product.
 *
 * The distinction the spec is emphatic about (§43, ADR-005) is encoded structurally: a `Supplier` is
 * where goods come from and a `ShippingProvider` is who moves them, and no field here can hold either
 * one interchangeably. "The supplier ships it themselves" is a boolean, not a merged entity.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/fulfillment`.
 */
import { PhOption, phOptions } from './productHub.core';
import { SHIPPING_RULE_LEVEL_LABELS, type ResolvedShippingRule, type ShippingRuleLevel } from './productHub.shipping';

/** Where the goods come from (§46). `multiple` means both a warehouse and a supplier can serve. */
export const FULFILLMENT_SOURCES = ['warehouse', 'supplier', 'multiple'] as const;
export type FulfillmentSource = (typeof FULFILLMENT_SOURCES)[number];

export const FULFILLMENT_SOURCE_LABELS: Record<FulfillmentSource, string> = {
  warehouse: 'מהמחסן שלנו',
  supplier: 'מהספק',
  multiple: 'מהמחסן או מהספק'
};

/* ------------------------------------------------------------------ */
/* The whole fulfillment answer                                        */
/* ------------------------------------------------------------------ */

/** `ProductFulfillmentSettings`, as the resolver needs it (doc 1 §41). */
export interface ProductFulfillmentRow {
  fulfillmentSource: FulfillmentSource;
  supplierId: number | null;
  /** True when the supplier ships directly and no provider of ours is involved (ADR-005). */
  supplierHandlesShipping: boolean;
  canShip: boolean;
  canPickup: boolean;
  /** False pins the product to its own method and skips the rule ladder entirely. */
  useShippingRule: boolean;
  shippingMethodOverrideId: number | null;
  shippingProviderOverrideId: number | null;
}

/**
 * What a product with no settings row behaves as.
 *
 * Shared rather than repeated because two places have to agree on it: the resolver's fallbacks and
 * the `GET` that hands the screen a form to fill in. If the API returned different defaults from the
 * ones the resolver applies, opening the form and pressing save would silently change behaviour —
 * the worst kind of no-op.
 */
export const DEFAULT_FULFILLMENT_SETTINGS: ProductFulfillmentRow = {
  fulfillmentSource: 'warehouse',
  supplierId: null,
  supplierHandlesShipping: false,
  canShip: true,
  canPickup: false,
  useShippingRule: true,
  shippingMethodOverrideId: null,
  shippingProviderOverrideId: null
};

/**
 * `StoreProductFulfillmentOverrides` (doc 1 §42). Every field nullable — null means "inherit".
 *
 * The handling flags below do not take part in the resolver's decision — nothing here picks a
 * carrier because something is fragile. They travel to a packing slip, so they are carried on the
 * row and surfaced by the store's advanced settings dialog rather than resolved.
 */
export interface StoreFulfillmentOverrideRow {
  useProductSettings: boolean;
  fulfillmentSource: FulfillmentSource | null;
  supplierId: number | null;
  canShip: boolean | null;
  canPickup: boolean | null;
  shippingMethodId: number | null;
  shippingProviderId: number | null;
  useShippingRule?: boolean | null;
  supplierHandlesShipping?: boolean | null;
  fragile?: boolean | null;
  specialHandling?: boolean | null;
  specialHandlingNote?: string | null;
  shipSeparately?: boolean | null;
  separateEachUnit?: boolean | null;
}

/**
 * Where the winning method came from. Ordered from most specific to least.
 *
 * `product_warehouse` is the product's own shipping card — the methods it may leave each of its
 * warehouses by. It sits **below** the rule ladder because it is a set of what is permitted rather
 * than a choice of one, so anything that actually names a method outranks it; and it exists at all
 * because without it the card was unreadable by the resolver. A product configured with "משלוח רגיל"
 * out of its warehouse resolved to "אין הגדרה", and the only screen that writes shipping had no
 * effect on the answer.
 */
export const FULFILLMENT_SOURCE_OF_TRUTH = [
  'store_override',
  'variant_override',
  'product_override',
  'rule',
  'product_warehouse',
  'none'
] as const;
export type FulfillmentDecisionSource = (typeof FULFILLMENT_SOURCE_OF_TRUTH)[number];

export const FULFILLMENT_DECISION_LABELS: Record<FulfillmentDecisionSource, string> = {
  store_override: 'דריסה של החנות',
  variant_override: 'הגדרת שילוח של הוריאציה',
  product_override: 'הגדרת שילוח של המוצר',
  rule: 'כלל שילוח',
  product_warehouse: 'שיטות האספקה של המוצר במחסן',
  none: 'אין הגדרה'
};

export interface ResolvedFulfillment {
  fulfillmentSource: FulfillmentSource;
  supplierId: number | null;
  supplierHandlesShipping: boolean;
  canShip: boolean;
  canPickup: boolean;
  shippingMethodId: number | null;
  shippingProviderId: number | null;
  /** Which layer decided the method — store → variant → product → rule. */
  decidedBy: FulfillmentDecisionSource;
  /** True when a variant row stood in for the product's, so a screen can say whose settings these are. */
  fromVariantSettings: boolean;
  decidedByLabel: string;
  /** The rung the rule came from, when a rule was consulted at all. */
  ruleLevel: ShippingRuleLevel;
  ruleLevelLabel: string;
  ruleId: number | null;
  /** Hebrew warnings for the summary screen — §47's "no delivery option" lives here. */
  warnings: string[];
}

/**
 * The full answer for one variant in one store (§41-42, §46-47).
 *
 * Precedence, most specific first: **the store's override → the variant's settings → the product's
 * settings → the rule**. Three properties of that are easy to get wrong:
 *
 *  1. **`useProductSettings` short-circuits the store layer entirely.** A store that has not opted
 *     out contributes nothing, even if stale values sit in its row — which they will, because the
 *     override row is kept rather than deleted when somebody toggles back.
 *  2. **A variant's row replaces the product's wholesale — it is not merged field by field.** Every
 *     column on `ProductFulfillmentSettings` is NOT NULL, so a variant row has no way to say
 *     "inherit this one field": every value in it is a stated value. The UI matches that shape —
 *     one switch per variant, then a complete panel — which is also how the store override's
 *     `useProductSettings` works one layer up. Merging would have meant asking of a `false` whether
 *     anyone meant it, and nothing in the row can answer that.
 *  3. **`useShippingRule = false` skips the ladder, it does not lose to it.** The product (or the
 *     variant, if it has its own row) is pinned to its own method, and the resolver says which.
 *
 * `canShip = false` **and** `canPickup = false` is not an error here. §47 asks for a *warning* —
 * it is a legitimate half-configured state during setup, and refusing it would block the wizard.
 */
export const resolveFulfillment = (input: {
  product: ProductFulfillmentRow | null;
  /** This variant's own row, when it has one. Replaces `product` entirely — see (2) above. */
  variant?: ProductFulfillmentRow | null;
  storeOverride: StoreFulfillmentOverrideRow | null;
  rule: ResolvedShippingRule;
  /** The method the rule's own row prefers, when the rule named no provider. */
  methodPreferredProviderId?: number | null;
  /**
   * The product's own shipping card, reduced to one pair.
   *
   * The card holds **sets** — which methods and carriers this product may leave its warehouses by —
   * and this is the caller's deterministic pick from them (see `warehouseFallback` on the server).
   * `shippingProviderId` is null whenever the set holds more than one, because "three carriers are
   * allowed" is not a decision that one of them was chosen.
   */
  warehouse?: { shippingMethodId: number | null; shippingProviderId: number | null } | null;
}): ResolvedFulfillment => {
  const fromVariantSettings = !!input.variant;
  // The row that speaks for this variant: its own if it has one, otherwise its product's.
  const base = input.variant ?? input.product;
  // A store row with `useProductSettings` on is not an override at all — see (1) above.
  const override = input.storeOverride?.useProductSettings === false ? input.storeOverride : null;
  const ruleConsulted = base === null || base.useShippingRule;

  const fulfillmentSource: FulfillmentSource = override?.fulfillmentSource ?? base?.fulfillmentSource ?? 'warehouse';
  const supplierId = override?.supplierId ?? base?.supplierId ?? null;
  const canShip = override?.canShip ?? base?.canShip ?? true;
  const canPickup = override?.canPickup ?? base?.canPickup ?? false;

  const ruleMethodId = ruleConsulted ? input.rule.rule?.shippingMethodId ?? null : null;
  const ruleProviderId = ruleConsulted ? input.rule.rule?.shippingProviderId ?? null : null;

  const shippingMethodId =
    override?.shippingMethodId ??
    (base && !base.useShippingRule ? base.shippingMethodOverrideId : null) ??
    ruleMethodId ??
    // The product's own shipping card, when nothing above it named a method. Last, because a set of
    // permitted methods is weaker than any statement that picked one.
    input.warehouse?.shippingMethodId ??
    null;

  const decidedByWarehouse =
    shippingMethodId !== null &&
    override?.shippingMethodId == null &&
    !(base && !base.useShippingRule && base.shippingMethodOverrideId != null) &&
    ruleMethodId == null;

  const shippingProviderId =
    override?.shippingProviderId ??
    (base && !base.useShippingRule ? base.shippingProviderOverrideId : null) ??
    ruleProviderId ??
    // Only when the **method** came from the card: a carrier from the warehouse row beside a method
    // a rule chose would be two different answers stitched together.
    (decidedByWarehouse ? input.warehouse?.shippingProviderId ?? null : null) ??
    // Last resort: the method's own preferred carrier. A rule that names only a method is normal —
    // "ship it by truck" is a decision that does not have to pick the haulier.
    input.methodPreferredProviderId ??
    null;

  const pinnedToOwnMethod = !!base && !base.useShippingRule && base.shippingMethodOverrideId != null;

  const decidedBy: FulfillmentDecisionSource =
    override?.shippingMethodId != null
      ? 'store_override'
      : pinnedToOwnMethod
      ? fromVariantSettings
        ? 'variant_override'
        : 'product_override'
      : ruleMethodId != null
      ? 'rule'
      : decidedByWarehouse
      ? 'product_warehouse'
      : 'none';

  return {
    fulfillmentSource,
    supplierId,
    supplierHandlesShipping: base?.supplierHandlesShipping ?? false,
    canShip,
    canPickup,
    shippingMethodId,
    shippingProviderId,
    decidedBy,
    decidedByLabel: FULFILLMENT_DECISION_LABELS[decidedBy],
    fromVariantSettings,
    ruleLevel: ruleConsulted ? input.rule.level : 'none',
    ruleLevelLabel: ruleConsulted ? input.rule.levelLabel : SHIPPING_RULE_LEVEL_LABELS.none,
    ruleId: ruleConsulted ? input.rule.rule?.id ?? null : null,
    warnings: fulfillmentWarnings({ canShip, canPickup, fulfillmentSource, supplierId, shippingMethodId })
  };
};

/**
 * The states §47 and §46 say to flag rather than forbid.
 *
 * All three are reachable mid-setup and none of them is a data error: a product may exist before its
 * shipping is configured. They are Hebrew sentences rather than codes because they are read, not
 * switched on — the anomaly list on the inventory dashboard has the code for the one that matters
 * system-wide.
 */
export const fulfillmentWarnings = (input: {
  canShip: boolean;
  canPickup: boolean;
  fulfillmentSource: FulfillmentSource;
  supplierId: number | null;
  shippingMethodId: number | null;
}): string[] => {
  const warnings: string[] = [];

  if (!input.canShip && !input.canPickup) warnings.push('אין דרך אספקה: המוצר אינו ניתן למשלוח ואינו לאיסוף עצמי');
  if (input.fulfillmentSource === 'supplier' && input.supplierId === null) {
    warnings.push('מקור האספקה הוא ספק, אך לא נבחר ספק');
  }
  if (input.canShip && input.shippingMethodId === null) warnings.push('אין שיטת אספקה שחלה על המוצר');

  return warnings;
};

/**
 * The handling flags of §48.
 *
 * Kept as a labelled list rather than four scattered booleans in the UI, so the product form and the
 * fulfillment screen name them identically — these end up on a packing slip, and "ship separately"
 * meaning two different things in two screens is a picking error waiting to happen.
 */
export const SHIPPING_ATTRIBUTES = ['fragile', 'specialHandling', 'shipSeparately', 'separateEachUnit'] as const;
export type ShippingAttribute = (typeof SHIPPING_ATTRIBUTES)[number];

export const SHIPPING_ATTRIBUTE_LABELS: Record<ShippingAttribute, string> = {
  fragile: 'שביר',
  specialHandling: 'דורש טיפול מיוחד',
  shipSeparately: 'נשלח בנפרד ממוצרים אחרים',
  separateEachUnit: 'כל יחידה נשלחת בנפרד'
};

export const SHIPPING_ATTRIBUTE_OPTIONS: PhOption<ShippingAttribute>[] = phOptions(
  SHIPPING_ATTRIBUTES,
  SHIPPING_ATTRIBUTE_LABELS
);

/** Delivery in one phrase, for a table cell. `canShip=false` + `canPickup=true` is pickup-only (§47). */
export const deliveryOptionsLabel = (canShip: boolean, canPickup: boolean): string => {
  if (canShip && canPickup) return 'משלוח או איסוף עצמי';
  if (canShip) return 'משלוח בלבד';
  if (canPickup) return 'איסוף עצמי בלבד';
  return 'אין דרך אספקה';
};
